import { FC, useState } from "react";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import {
	useGetPrograms,
	useCreateProgram,
	useUpdateProgram,
} from "../../hooks/usePrograms";
import { useGetTreatmentPlans } from "../../hooks/useTreatmentPlans";
import { useGetOrganizationList } from "../../hooks/useOrganizations";
import { ProgramRequest, Program } from "../../domain/program.interface";
import { OrganizationResponse } from "../../domain/organization.interface";
import EditIcon from "@mui/icons-material/Edit";
import { useGetActiveRolesByAccountId } from "../../hooks/useRoles";
import { useActivePayers } from "../../hooks/userPayers";
import { PayerResponse } from "../../domain/payer.interface";

const ProgramsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const defaultAccountId =
		process.env.REACT_APP_ACCOUNT_ID || "507f1f77bcf86cd799439011";

	const [showCreateForm, setShowCreateForm] = useState(false);
	const [formData, setFormData] = useState<
		ProgramRequest & { cmRoles: string[]; additionalStaffRoles: string[] }
	>({
		accountId: defaultAccountId,
		name: "",
		type: "OUD",
		status: "ACTIVE",
		multiTreatmentPlan: false,
		treatmentPlanChangeAllowed: false,
		treatmentPlan: "",
		orgUnitId: [],
		parameters: {
			eligibility: true,
			consent: true,
			maxBeneficiaries: 100,
			durationInWeeks: 12,
			maxYearlyEarnings: 5000,
			budgetLimit: 10000,
			baseIncentiveNegative: 25,
			baseIncentivePositive: 50,
			biVisitIncrementNegative: 10,
			repetitionPerSteps: 3,
			eligibilityConfirmationIntervalDays: undefined,
		},
		createdBy: userInfo?.id || "",
		cmRoles: [],
		additionalStaffRoles: [],
		payers: [],
		incentiveOnDemand: false,
		onDemandIncentiveMin: undefined,
		onDemandIncentiveMax: undefined,
	});

	// State for selected organization units
	const [selectedOrgUnits, setSelectedOrgUnits] = useState<
		OrganizationResponse[]
	>([]);

	// State for selected payers
	const [selectedPayers, setSelectedPayers] = useState<PayerResponse[]>([]);

	const {
		data: programs,
		isLoading,
		error,
		refetch,
	} = useGetPrograms(defaultAccountId);
	const createMutation = useCreateProgram();
	const updateMutation = useUpdateProgram();
	const [editingProgramId, setEditingProgramId] = useState<string | null>(null);

	// Fetch treatment plans for dropdown
	const { data: treatmentPlans, isLoading: treatmentPlansLoading } =
		useGetTreatmentPlans();

	// Fetch organizations for dropdown
	const { data: organizationsData, isLoading: organizationsLoading } =
		useGetOrganizationList(defaultAccountId);

	// Fetch active payers for dropdown
	const { data: payersData, isLoading: payersLoading } = useActivePayers();

	const handleInputChange = (field: string, value: string | boolean) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleParameterChange = (field: string, value: number | boolean) => {
		setFormData((prev) => {
			const updatedParameters = { ...prev.parameters, [field]: value };
			if (field === "eligibility" && value === false) {
				updatedParameters.eligibilityConfirmationIntervalDays = undefined;
			}
			return { ...prev, parameters: updatedParameters };
		});
	};

	const resetForm = () => {
		setSelectedOrgUnits([]);
		setSelectedPayers([]);
		setFormData({
			accountId: defaultAccountId,
			name: "",
			type: "OUD",
			status: "ACTIVE",
			multiTreatmentPlan: false,
			treatmentPlanChangeAllowed: false,
			treatmentPlan: "",
			orgUnitId: [],
			parameters: {
				eligibility: true,
				consent: true,
				maxBeneficiaries: 100,
				durationInWeeks: 12,
				maxYearlyEarnings: 5000,
				budgetLimit: 10000,
				baseIncentiveNegative: 25,
				baseIncentivePositive: 50,
				biVisitIncrementNegative: 10,
				repetitionPerSteps: 3,
			},
			createdBy: userInfo?.id || "",
			cmRoles: [],
			additionalStaffRoles: [],
			payers: [],
			incentiveOnDemand: false,
			onDemandIncentiveMin: undefined,
			onDemandIncentiveMax: undefined,
		});
	};

	const handleEditProgram = (program: Program) => {
		setShowCreateForm(true);
		setEditingProgramId(program._id);
		setFormData({
			accountId: program.accountId,
			name: program.name,
			type: program.type,
			status: program.status,
			multiTreatmentPlan: program.multiTreatmentPlan,
			treatmentPlanChangeAllowed: program.treatmentPlanChangeAllowed,
			treatmentPlan: program.treatmentPlan || "",
			orgUnitId: program.orgUnitId || [],
			parameters: normalizeParameters(program.parameters),
			createdBy: program.createdBy,
			cmRoles: program.cmRoles || [],
			additionalStaffRoles: program.additionalStaffRoles || [],
			payers: program.payers || [],
			incentiveOnDemand: program.incentiveOnDemand || false,
			onDemandIncentiveMin: program.onDemandIncentiveMin,
			onDemandIncentiveMax: program.onDemandIncentiveMax,
		});
		// Set selected org units for Autocomplete
		if (organizationsData) {
			setSelectedOrgUnits(
				organizationsData.filter((org) =>
					(program.orgUnitId || []).includes(org._id),
				),
			);
		}
		// Set selected payers for Autocomplete
		if (payersData) {
			setSelectedPayers(
				payersData.filter((payer) =>
					(program.payers || []).includes(payer._id),
				),
			);
		}
	};
	// Fetch roles for dropdowns
	const { data: rolesData = [], isLoading: rolesLoading } =
		useGetActiveRolesByAccountId();

	const handleSubmit = async () => {
		if (
			formData.parameters.eligibility &&
			(!formData.parameters.eligibilityConfirmationIntervalDays ||
				formData.parameters.eligibilityConfirmationIntervalDays < 1)
		) {
			showSnackBar(
				"Eligibility Reconfirmation Interval (days) is required and must be at least 1.",
				"error",
			);
			return;
		}

		// Remove _id from parameters before sending
		const cleanParameters = { ...formData.parameters };
		delete cleanParameters._id;

		// Clean durationConfig if it exists
		const cleanDurationConfig = cleanParameters.durationConfig
			? // eslint-disable-next-line @typescript-eslint/no-explicit-any
				cleanParameters.durationConfig.map((d: any) => {
					const cleanD = { ...d };
					delete cleanD._id;
					return cleanD;
				})
			: [];

		const cleanFormData = {
			...formData,
			parameters: {
				...cleanParameters,
				...(cleanDurationConfig.length > 0 && {
					durationConfig: cleanDurationConfig,
				}),
			},
		};

		try {
			if (editingProgramId) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const updateData: any = { ...cleanFormData };
				delete updateData.createdBy;
				updateData.updatedBy = userInfo?.id || "";

				if (
					updateData.onDemandIncentiveMin !== undefined &&
					updateData.onDemandIncentiveMin !== ""
				) {
					updateData.onDemandIncentiveMin = Number(
						updateData.onDemandIncentiveMin,
					);
				}
				if (
					updateData.onDemandIncentiveMax !== undefined &&
					updateData.onDemandIncentiveMax !== ""
				) {
					updateData.onDemandIncentiveMax = Number(
						updateData.onDemandIncentiveMax,
					);
				}

				await updateMutation.mutateAsync({
					programId: editingProgramId,
					programData: updateData,
				});
				setEditingProgramId(null);
			} else {
				// Create
				await createMutation.mutateAsync({
					accountId: defaultAccountId,
					programData: cleanFormData,
				});
			}
			setShowCreateForm(false);
		} catch (err) {
			const errorMsg =
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(err as any)?.message?.join?.(", ") ||
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(err as any)?.message ||
				"An error occurred while saving the program";
			showSnackBar(errorMsg, "error");
		}
		resetForm();
		refetch();
	};

	if (isLoading) return <Typography>Loading programs...</Typography>;
	if (error)
		return <Typography color="error">Error loading programs</Typography>;

	// Helper function to get treatment plan name by ID
	const getTreatmentPlanName = (treatmentPlanId?: string): string => {
		if (!treatmentPlanId) return "None";
		const plan = treatmentPlans?.find((tp) => tp._id === treatmentPlanId);
		return plan?.name || `Treatment Plan ${treatmentPlanId.slice(-8)}`;
	};

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Program Management
				</Typography>
				<Button
					variant="contained"
					onClick={() => setShowCreateForm(!showCreateForm)}
				>
					{showCreateForm ? "Cancel" : "Create New Program"}
				</Button>
			</Box>

			{showCreateForm && (
				<Paper sx={{ padding: 3, marginBottom: 3 }}>
					<Typography variant="h6" gutterBottom>
						{editingProgramId ? "Edit Program" : "Create New Program"}
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Program Name"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Type</InputLabel>
								<Select
									value={formData.type || "Legacy"}
									label="Type"
									onChange={(e) => handleInputChange("type", e.target.value)}
								>
									<MenuItem value="LEGACY">Legacy</MenuItem>
									<MenuItem value="OUD">OUD</MenuItem>
									<MenuItem value="STIM">Stim</MenuItem>
									<MenuItem value="OTHER">Other</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Status</InputLabel>
								<Select
									value={formData.status}
									label="Status"
									onChange={(e) => handleInputChange("status", e.target.value)}
								>
									<MenuItem value="ACTIVE">Active</MenuItem>
									<MenuItem value="INACTIVE">Inactive</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						{/* Created By field removed: now set automatically from userInfo */}
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Treatment Plan (Optional)</InputLabel>
								<Select
									value={formData.treatmentPlan}
									label="Treatment Plan (Optional)"
									onChange={(e) =>
										handleInputChange("treatmentPlan", e.target.value)
									}
									disabled={treatmentPlansLoading}
								>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{treatmentPlans?.map((treatmentPlan) => (
										<MenuItem key={treatmentPlan._id} value={treatmentPlan._id}>
											{treatmentPlan.name ||
												`Treatment Plan ${treatmentPlan._id.slice(-8)}`}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.multiTreatmentPlan}
										onChange={(e) =>
											handleInputChange("multiTreatmentPlan", e.target.checked)
										}
									/>
								}
								label="Multi Treatment Plan"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.treatmentPlanChangeAllowed}
										onChange={(e) =>
											handleInputChange(
												"treatmentPlanChangeAllowed",
												e.target.checked,
											)
										}
									/>
								}
								label="Treatment Plan Change Allowed"
							/>
						</Grid>

						{/* Parameters Section */}
						<Grid item xs={12}>
							<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
								Program Parameters
							</Typography>
						</Grid>

						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.parameters.eligibility}
										onChange={(e) =>
											handleParameterChange("eligibility", e.target.checked)
										}
									/>
								}
								label="Eligibility Required"
							/>
						</Grid>
						{formData.parameters.eligibility && (
							<Grid item xs={12} md={6}>
								<TextField
									type="number"
									label="Eligibility Reconfirmation Interval (days)"
									value={
										formData.parameters.eligibilityConfirmationIntervalDays ??
										""
									}
									onChange={(e) => {
										const raw = e.target.value;
										if (raw === "") {
											handleParameterChange(
												"eligibilityConfirmationIntervalDays",
												undefined as unknown as number,
											);
											return;
										}
										const parsed = parseInt(raw, 10);
										if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
											handleParameterChange(
												"eligibilityConfirmationIntervalDays",
												parsed,
											);
										}
									}}
									inputProps={{ min: 1, max: 100 }}
									required
									error={
										formData.parameters.eligibility &&
										!formData.parameters.eligibilityConfirmationIntervalDays
									}
									fullWidth
									size="small"
								/>
							</Grid>
						)}
						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.parameters.consent}
										onChange={(e) =>
											handleParameterChange("consent", e.target.checked)
										}
									/>
								}
								label="Consent Required"
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.incentiveOnDemand || false}
										onChange={(e) =>
											handleInputChange("incentiveOnDemand", e.target.checked)
										}
									/>
								}
								label="On Demand Incentive"
							/>
						</Grid>
						{formData.incentiveOnDemand && (
							<>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										label="On Demand Min Amount"
										type="number"
										value={formData.onDemandIncentiveMin ?? ""}
										onChange={(e) =>
											handleInputChange("onDemandIncentiveMin", e.target.value)
										}
										inputProps={{ min: 0, step: "0.01" }}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										label="On Demand Max Amount"
										type="number"
										value={formData.onDemandIncentiveMax ?? ""}
										onChange={(e) =>
											handleInputChange("onDemandIncentiveMax", e.target.value)
										}
										inputProps={{ min: 0, step: "0.01" }}
									/>
								</Grid>
							</>
						)}

						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Max Beneficiaries"
								type="number"
								value={formData.parameters.maxBeneficiaries}
								onChange={(e) =>
									handleParameterChange(
										"maxBeneficiaries",
										parseInt(e.target.value),
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Duration (Weeks)"
								type="number"
								value={formData.parameters.durationInWeeks}
								onChange={(e) =>
									handleParameterChange(
										"durationInWeeks",
										parseInt(e.target.value),
									)
								}
								required
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Max Yearly Earnings"
								type="number"
								value={formData.parameters.maxYearlyEarnings}
								onChange={(e) =>
									handleParameterChange(
										"maxYearlyEarnings",
										parseInt(e.target.value),
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Budget Limit"
								type="number"
								value={formData.parameters.budgetLimit}
								onChange={(e) =>
									handleParameterChange("budgetLimit", parseInt(e.target.value))
								}
								required
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Base Incentive Negative"
								type="number"
								value={formData.parameters.baseIncentiveNegative}
								onChange={(e) =>
									handleParameterChange(
										"baseIncentiveNegative",
										parseFloat(e.target.value),
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Base Incentive Positive"
								type="number"
								value={formData.parameters.baseIncentivePositive}
								onChange={(e) =>
									handleParameterChange(
										"baseIncentivePositive",
										parseFloat(e.target.value),
									)
								}
								required
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Bi-Visit Increment Negative"
								type="number"
								value={formData.parameters.biVisitIncrementNegative}
								onChange={(e) =>
									handleParameterChange(
										"biVisitIncrementNegative",
										parseFloat(e.target.value),
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Repetition Per Steps"
								type="number"
								value={formData.parameters.repetitionPerSteps}
								onChange={(e) =>
									handleParameterChange(
										"repetitionPerSteps",
										parseInt(e.target.value),
									)
								}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={organizationsData || []}
								getOptionLabel={(option) => option.name}
								value={selectedOrgUnits}
								onChange={(_, newValue) => {
									setSelectedOrgUnits(newValue);
									setFormData({
										...formData,
										orgUnitId: newValue.map((org) => org._id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Organization Units"
										placeholder="Select organization units"
									/>
								)}
								disabled={organizationsLoading}
							/>
						</Grid>

						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={payersData || []}
								getOptionLabel={(option) => option.name}
								value={selectedPayers}
								onChange={(_, newValue) => {
									setSelectedPayers(newValue);
									setFormData({
										...formData,
										payers: newValue.map((payer) => payer._id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Payer Plans"
										placeholder="Select payer plans that will be available for this program"
										helperText="Choose the active payer plans that beneficiaries can select during enrollment"
									/>
								)}
								disabled={payersLoading}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Autocomplete
								multiple
								options={rolesData}
								getOptionLabel={(option) => option.name}
								value={rolesData.filter((role) =>
									formData.cmRoles?.includes(role.id),
								)}
								onChange={(_, newValue) => {
									setFormData({
										...formData,
										cmRoles: newValue.map((role) => role.id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Case Manager Roles"
										placeholder="Select case manager roles"
									/>
								)}
								disabled={rolesLoading}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Autocomplete
								multiple
								options={rolesData}
								getOptionLabel={(option) => option.name}
								value={rolesData.filter((role) =>
									formData.additionalStaffRoles?.includes(role.id),
								)}
								onChange={(_, newValue) => {
									setFormData({
										...formData,
										additionalStaffRoles: newValue.map((role) => role.id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Additional Staff Roles"
										placeholder="Select additional staff roles"
									/>
								)}
								disabled={rolesLoading}
							/>
						</Grid>
						<Grid item xs={12}>
							<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
								<Button
									variant="outlined"
									onClick={() => {
										setShowCreateForm(false);
										setEditingProgramId(null);
										resetForm();
									}}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={handleSubmit}
									disabled={
										createMutation.isPending || updateMutation.isPending
									}
								>
									{editingProgramId
										? updateMutation.isPending
											? "Updating..."
											: "Update Program"
										: createMutation.isPending
											? "Creating..."
											: "Create Program"}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Paper>
			)}

			<Grid container spacing={3}>
				{programs?.map((program: Program) => (
					<Grid item xs={12} md={6} lg={4} key={program._id}>
						<Card>
							<CardContent>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									mb={2}
								>
									<Typography variant="h6">{program.name}</Typography>
									<Box display="flex" gap={1}>
										<Chip
											label={program.type || "Legacy"}
											color="primary"
											size="small"
										/>
										<Chip
											label={program.status}
											color={
												program.status === "ACTIVE" ? "success" : "default"
											}
											size="small"
										/>
										<Button
											size="small"
											variant="outlined"
											startIcon={<EditIcon />}
											onClick={() => handleEditProgram(program)}
										>
											Edit
										</Button>
									</Box>
								</Box>
								<Typography variant="body2" color="text.secondary" paragraph>
									Max Beneficiaries: {program.parameters.maxBeneficiaries}
								</Typography>
								<Typography variant="body2" color="text.secondary" paragraph>
									Duration: {program.parameters.durationInWeeks} weeks
								</Typography>
								{program.treatmentPlan && (
									<Typography variant="body2" color="text.secondary" paragraph>
										Treatment Plan:{" "}
										{getTreatmentPlanName(program.treatmentPlan)}
									</Typography>
								)}
								{program.orgUnitId && program.orgUnitId.length > 0 && (
									<Typography variant="body2" color="text.secondary" paragraph>
										Organization Units: {program.orgUnitId.length}
									</Typography>
								)}
								{program.payers && program.payers.length > 0 && (
									<Typography variant="body2" color="text.secondary" paragraph>
										Payer Plans: {program.payers.length}
									</Typography>
								)}
								<Typography variant="caption" display="block" gutterBottom>
									Created: {new Date(program.createdAt).toLocaleDateString()}
								</Typography>
								<Typography variant="caption" display="block">
									ID: {program._id}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>
		</Box>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeParameters(params: any) {
	const keysToNormalize = [
		"budgetLimit",
		"baseIncentiveNegative",
		"baseIncentivePositive",
		"biVisitIncrementNegative",
		"maxYearlyEarnings",
		"maxBeneficiaries",
		"durationInWeeks",
		"repetitionPerSteps",
	];
	const normalized = { ...params };
	for (const key of keysToNormalize) {
		if (
			normalized[key] &&
			typeof normalized[key] === "object" &&
			normalized[key].$numberDecimal
		) {
			normalized[key] = Number(normalized[key].$numberDecimal);
		}
	}
	return normalized;
}

export default ProgramsComponent;
